import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Loader2, Plus, RefreshCw, Trash2, Download, Gift, Copy } from "lucide-react";
import html2canvas from "html2canvas";

export function PromoCodesManager() {
    const [codes, setCodes] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const { toast } = useToast();

    // Create/Edit Form State
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [formData, setFormData] = useState({
        code: "",
        discount_type: "PERCENTAGE", // PERCENTAGE or FIXED_AMOUNT
        discount_value: "",
        max_discount_amount: "",
        min_order_amount: "0",
        start_date: "",
        end_date: "",
        total_usage_limit: "",
        per_user_limit: "1",
        is_active: true
    });

    // Image Generation State
    const [previewCode, setPreviewCode] = useState<any>(null);
    const cardRef = useRef<HTMLDivElement>(null);

    const [brandName, setBrandName] = useState("Amadeo Photography");

    // Redemption Tracking State
    const [redemptions, setRedemptions] = useState<Record<string, any[]>>({});
    const [selectedCodeForRedemptions, setSelectedCodeForRedemptions] = useState<string | null>(null);

    useEffect(() => {
        fetchCodes();
        fetchBrandName();
    }, []);

    const fetchBrandName = async () => {
        const { data } = await supabase.from('photographer_profiles').select('display_name').limit(1).single();
        if (data?.display_name) {
            setBrandName(data.display_name);
        }
    };

    const fetchCodes = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('promo_codes')
            .select('*')
            .order('created_at', { ascending: false });

        if (data) {
            // Fetch redemptions for each code
            const redemptionsMap: Record<string, any[]> = {};
            for (const code of data) {
                const { data: redemptionData } = await supabase
                    .from('promo_code_redemptions')
                    .select('*, bookings(customer_name, customer_email, created_at)')
                    .eq('promo_code_id', code.id)
                    .order('redeemed_at', { ascending: false });

                redemptionsMap[code.id] = redemptionData || [];
            }
            setRedemptions(redemptionsMap);
        }

        if (error) {
            toast({
                title: "Error fetching codes",
                description: error.message,
                variant: "destructive"
            });
        } else {
            setCodes(data || []);
        }
        setLoading(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const generateRandomCode = () => {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let result = "";
        for (let i = 0; i < 8; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setFormData(prev => ({ ...prev, code: result }));
    };

    const handleSubmit = async () => {
        if (!formData.code || !formData.discount_value) {
            toast({ title: "Validation Error", description: "Code and Discount Value are required", variant: "destructive" });
            return;
        }

        const payload = {
            code: formData.code.toUpperCase(),
            discount_type: formData.discount_type,
            discount_value: parseFloat(formData.discount_value),
            max_discount_amount: formData.max_discount_amount ? parseFloat(formData.max_discount_amount) : null,
            min_order_amount: parseFloat(formData.min_order_amount) || 0,
            start_date: formData.start_date || null,
            end_date: formData.end_date || null,
            total_usage_limit: formData.total_usage_limit ? parseInt(formData.total_usage_limit) : null,
            per_user_limit: parseInt(formData.per_user_limit) || 1,
            is_active: formData.is_active
        };

        // Validate date range
        if (payload.start_date && payload.end_date) {
            const startDate = new Date(payload.start_date);
            const endDate = new Date(payload.end_date);
            if (startDate >= endDate) {
                toast({
                    title: "Invalid Date Range",
                    description: "Start date must be before end date",
                    variant: "destructive"
                });
                return;
            }
        }

        const { error } = await supabase.from('promo_codes').insert([payload]);

        if (error) {
            toast({ title: "Error creating code", description: error.message, variant: "destructive" });
        } else {
            toast({ title: "Success", description: "Promo code created successfully" });
            setIsDialogOpen(false);
            fetchCodes();
            // Reset form
            setFormData({
                code: "",
                discount_type: "PERCENTAGE",
                discount_value: "",
                max_discount_amount: "",
                min_order_amount: "0",
                start_date: "",
                end_date: "",
                total_usage_limit: "",
                per_user_limit: "1",
                is_active: true
            });
        }
    };

    const toggleStatus = async (id: string, currentStatus: boolean) => {
        const { error } = await supabase.from('promo_codes').update({ is_active: !currentStatus }).eq('id', id);
        if (!error) fetchCodes();
    };

    const deleteCode = async (id: string) => {
        if (!confirm("Are you sure you want to delete this code?")) return;
        const { error } = await supabase.from('promo_codes').delete().eq('id', id);
        if (error) {
            toast({ title: "Error", description: "Could not delete (used codes cannot be deleted)", variant: "destructive" });
        } else {
            fetchCodes();
        }
    };

    const handleDownloadImage = async (code: any) => {
        setPreviewCode(code);
        // Give time for the modal/div to render
        setTimeout(async () => {
            if (cardRef.current) {
                try {
                    const canvas = await html2canvas(cardRef.current, {
                        backgroundColor: null,
                        scale: 2 // Higher resolution
                    });
                    const link = document.createElement('a');
                    link.download = `PROMO-${code.code}.png`;
                    link.href = canvas.toDataURL('image/png');
                    link.click();
                    toast({ title: "Downloaded", description: "Promo card image saved." });
                } catch (err) {
                    console.error(err);
                    toast({ title: "Error", description: "Failed to generate image", variant: "destructive" });
                }
            }
        }, 500);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight">Promo Codes</h2>
                    <p className="text-muted-foreground">Manage discounts and generate marketing assets.</p>
                </div>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button><Plus className="mr-2 h-4 w-4" /> Create New Code</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>Create Promo Code</DialogTitle>
                            <DialogDescription>Set up discount rules and limits.</DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-2 gap-4 py-4">
                            <div className="space-y-2 col-span-2 sm:col-span-1">
                                <Label>Promo Code</Label>
                                <div className="flex gap-2">
                                    <Input
                                        name="code"
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        placeholder="e.g. SUMMER20"
                                        className="uppercase"
                                    />
                                    <Button variant="outline" size="icon" onClick={generateRandomCode} type="button">
                                        <RefreshCw className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            <div className="space-y-2 col-span-2 sm:col-span-1">
                                <Label>Active Status</Label>
                                <div className="flex items-center gap-2 mt-2">
                                    <Switch
                                        checked={formData.is_active}
                                        onCheckedChange={(c) => setFormData(prev => ({ ...prev, is_active: c }))}
                                    />
                                    <span className="text-sm text-muted-foreground">{formData.is_active ? "Active" : "Inactive"}</span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Discount Type</Label>
                                <Select
                                    value={formData.discount_type}
                                    onValueChange={(v) => setFormData(prev => ({ ...prev, discount_type: v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="PERCENTAGE">Percentage (%)</SelectItem>
                                        <SelectItem value="FIXED_AMOUNT">Fixed Amount (€)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Value</Label>
                                <Input
                                    name="discount_value"
                                    type="number"
                                    value={formData.discount_value}
                                    onChange={handleInputChange}
                                    placeholder={formData.discount_type === 'PERCENTAGE' ? "20" : "50"}
                                />
                            </div>

                            {formData.discount_type === 'PERCENTAGE' && (
                                <div className="space-y-2">
                                    <Label>Max Discount Amount (Optional)</Label>
                                    <Input
                                        name="max_discount_amount"
                                        type="number"
                                        value={formData.max_discount_amount}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 100 max limit"
                                    />
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label>Min Order Amount</Label>
                                <Input
                                    name="min_order_amount"
                                    type="number"
                                    value={formData.min_order_amount}
                                    onChange={handleInputChange}
                                    placeholder="0"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Start Date (Optional)</Label>
                                <Input
                                    name="start_date"
                                    type="date"
                                    value={formData.start_date}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>End Date (Optional)</Label>
                                <Input
                                    name="end_date"
                                    type="date"
                                    value={formData.end_date}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Total Usage Limit</Label>
                                <Input
                                    name="total_usage_limit"
                                    type="number"
                                    value={formData.total_usage_limit}
                                    onChange={handleInputChange}
                                    placeholder="Unlimited"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Limit Per User</Label>
                                <Input
                                    name="per_user_limit"
                                    type="number"
                                    value={formData.per_user_limit}
                                    onChange={handleInputChange}
                                    placeholder="1"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button onClick={handleSubmit}>Create Promo Code</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {loading ? (
                <div className="flex justify-center p-8"><Loader2 className="animate-spin h-8 w-8 text-primary" /></div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {codes.map((code) => (
                        <Card key={code.id} className={`relative overflow-hidden border-l-4 ${code.is_active ? 'border-l-primary' : 'border-l-muted'}`}>
                            {!code.is_active && <div className="absolute inset-0 bg-background/50 z-10 pointer-events-none" />}

                            <CardHeader className="pb-2">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <CardTitle className="font-mono text-xl tracking-wider select-all cursor-pointer flex items-center gap-2"
                                            onClick={() => {
                                                navigator.clipboard.writeText(code.code);
                                                toast({ description: "Copied to clipboard" });
                                            }}>
                                            {code.code} <Copy className="h-3 w-3 text-muted-foreground opacity-50" />
                                        </CardTitle>
                                        <CardDescription>{code.discount_type === 'PERCENTAGE' ? `${code.discount_value}% OFF` : `€${code.discount_value} OFF`}</CardDescription>
                                    </div>
                                    <div className="flex flex-col gap-1 items-end">
                                        <Badge variant={code.is_active ? "default" : "secondary"}>
                                            {code.is_active ? "Active" : "Inactive"}
                                        </Badge>
                                        {code.end_date && new Date(code.end_date) < new Date() && (
                                            <Badge variant="destructive" className="text-[10px]">
                                                Expired
                                            </Badge>
                                        )}
                                        {code.end_date && new Date(code.end_date) > new Date() &&
                                            new Date(code.end_date) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) && (
                                                <Badge variant="outline" className="text-[10px] border-yellow-500 text-yellow-600">
                                                    Expiring Soon
                                                </Badge>
                                            )}
                                        {code.total_usage_limit &&
                                            code.current_usage_count >= code.total_usage_limit * 0.9 &&
                                            code.current_usage_count < code.total_usage_limit && (
                                                <Badge variant="outline" className="text-[10px] border-orange-500 text-orange-600">
                                                    90% Used
                                                </Badge>
                                            )}
                                        {code.total_usage_limit &&
                                            code.current_usage_count >= code.total_usage_limit && (
                                                <Badge variant="destructive" className="text-[10px]">
                                                    Limit Reached
                                                </Badge>
                                            )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="text-sm space-y-2 pb-2">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Used:</span>
                                    <span className="font-medium">{code.current_usage_count} / {code.total_usage_limit || "∞"}</span>
                                </div>
                                {redemptions[code.id] && redemptions[code.id].length > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Revenue Impact:</span>
                                        <span className="font-medium text-green-600">
                                            -€{redemptions[code.id].reduce((sum, r) => sum + (r.discount_amount || 0), 0).toFixed(2)}
                                        </span>
                                    </div>
                                )}
                                {code.min_order_amount > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Min Order:</span>
                                        <span>€{code.min_order_amount}</span>
                                    </div>
                                )}
                                {(code.start_date || code.end_date) && (
                                    <div className="text-xs text-muted-foreground mt-2">
                                        {code.start_date && <>From: {format(new Date(code.start_date), 'dd MMM yyyy')}<br /></>}
                                        {code.end_date && <>Expires: {format(new Date(code.end_date), 'dd MMM yyyy')}</>}
                                    </div>
                                )}
                            </CardContent>
                            <CardFooter className="pt-2 flex justify-end gap-2 border-t bg-muted/20">
                                <Button variant="ghost" size="sm" onClick={() => toggleStatus(code.id, code.is_active)}>
                                    {code.is_active ? "Deactivate" : "Activate"}
                                </Button>
                                {redemptions[code.id] && redemptions[code.id].length > 0 && (
                                    <Dialog>
                                        <DialogTrigger asChild>
                                            <Button variant="outline" size="sm">
                                                View Redemptions ({redemptions[code.id].length})
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                            <DialogHeader>
                                                <DialogTitle>Redemptions for {code.code}</DialogTitle>
                                                <DialogDescription>
                                                    Total discount given: €{redemptions[code.id].reduce((sum, r) => sum + (r.discount_amount || 0), 0).toFixed(2)}
                                                </DialogDescription>
                                            </DialogHeader>
                                            <div className="space-y-2">
                                                {redemptions[code.id].map((redemption: any) => (
                                                    <div key={redemption.id} className="border rounded-lg p-3 text-sm">
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <p className="font-medium">{redemption.bookings?.customer_name || 'Unknown'}</p>
                                                                <p className="text-xs text-muted-foreground">{redemption.customer_email}</p>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="font-bold text-green-600">-€{redemption.discount_amount}</p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {format(new Date(redemption.redeemed_at), 'dd MMM yyyy')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                )}
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <Button variant="outline" size="sm" onClick={() => setPreviewCode(code)}>
                                            <Download className="h-4 w-4 mr-1" /> Save Card
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-md">
                                        <DialogHeader>
                                            <DialogTitle>Download Promo Card</DialogTitle>
                                        </DialogHeader>
                                        <div className="flex justify-center py-6 bg-gray-100 rounded-lg overflow-hidden">
                                            {/* THE CARD DESIGN TO BE CAPTURED */}
                                            <div
                                                ref={cardRef}
                                                className="w-[350px] aspect-[1.58/1] bg-gradient-to-br from-gray-900 to-black text-white p-6 pb-8 rounded-xl shadow-2xl relative flex flex-col justify-between overflow-hidden"
                                                style={{
                                                    backgroundImage: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                                                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)'
                                                }}
                                            >
                                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />
                                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full blur-2xl -ml-10 -mb-10 pointer-events-none" />

                                                {/* Header */}
                                                <div className="flex items-center gap-3 z-10">
                                                    <Gift className="h-8 w-8 text-yellow-500" />
                                                    <div>
                                                        <h3 className="font-bold text-lg leading-none tracking-wide text-gray-100">GIFT VOUCHER</h3>
                                                        <p className="text-[10px] text-gray-400 uppercase tracking-widest">{brandName}</p>
                                                    </div>
                                                </div>

                                                {/* Center Value */}
                                                <div className="text-center z-10 my-2">
                                                    <div className="text-5xl font-bold tracking-tighter text-yellow-400 drop-shadow-sm">
                                                        {previewCode?.discount_type === 'PERCENTAGE' ? `${previewCode.discount_value}%` : `€${previewCode?.discount_value}`}
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-300 tracking-[0.2em] uppercase mt-1">OFF YOUR NEXT SESSION</div>
                                                </div>

                                                {/* Code */}
                                                <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 text-center border border-white/10 z-10">
                                                    <p className="text-[10px] text-gray-400 mb-1">USE CODE AT CHECKOUT</p>
                                                    <p className="text-2xl font-mono font-bold tracking-widest text-white">{previewCode?.code}</p>
                                                </div>

                                                {/* Footer Info */}
                                                <div className="z-10 flex flex-col items-center gap-1">
                                                    {previewCode?.end_date ? (
                                                        <div className="text-[10px] font-bold text-yellow-400 uppercase tracking-[0.2em] bg-yellow-500/10 py-1.5 px-4 rounded-full border border-yellow-500/20 shadow-inner">
                                                            VALID TILL {format(new Date(previewCode.end_date), 'dd MMM yyyy')}
                                                        </div>
                                                    ) : (
                                                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">
                                                            NO EXPIRY
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <DialogFooter>
                                            <Button onClick={() => handleDownloadImage(previewCode)} className="w-full">
                                                <Download className="mr-2 h-4 w-4" /> Download PNG
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>
                                <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => deleteCode(code.id)}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </CardFooter>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
